// One audio element per episode page, shared by the audio card (which
// renders it) and the transcript (which seeks it and follows along).

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

interface PlayerValue {
  /** Attach to the page's <audio>. */
  ref: (el: HTMLAudioElement | null) => void;
  /** Current position in ms, updated every animation frame while playing. */
  currentMs: number;
  playing: boolean;
  seek: (ms: number, play?: boolean) => void;
}

const PlayerContext = createContext<PlayerValue | null>(null);

export function PlayerProvider({ children }: { children: ReactNode }) {
  const [el, setEl] = useState<HTMLAudioElement | null>(null);
  const [currentMs, setCurrentMs] = useState(0);
  const [playing, setPlaying] = useState(false);
  const frame = useRef<number | null>(null);

  useEffect(() => {
    if (!el) return;
    const tick = () => {
      setCurrentMs(el.currentTime * 1000);
      frame.current = requestAnimationFrame(tick);
    };
    const onPlay = () => {
      setPlaying(true);
      frame.current = requestAnimationFrame(tick);
    };
    const stop = () => {
      setPlaying(false);
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      setCurrentMs(el.currentTime * 1000);
    };
    const onSeeked = () => setCurrentMs(el.currentTime * 1000);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', stop);
    el.addEventListener('ended', stop);
    el.addEventListener('seeked', onSeeked);
    return () => {
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', stop);
      el.removeEventListener('ended', stop);
      el.removeEventListener('seeked', onSeeked);
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    };
  }, [el]);

  const seek = useCallback(
    (ms: number, play = true) => {
      if (!el) return;
      el.currentTime = ms / 1000;
      setCurrentMs(ms);
      // play() returns undefined in some environments; autoplay may be refused.
      if (play) void el.play()?.catch(() => undefined);
    },
    [el],
  );

  const value = useMemo(() => ({ ref: setEl, currentMs, playing, seek }), [currentMs, playing, seek]);
  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}

const NOOP: PlayerValue = {
  ref: () => undefined,
  currentMs: 0,
  playing: false,
  seek: () => undefined,
};

export function usePlayer(): PlayerValue {
  return useContext(PlayerContext) ?? NOOP;
}
